import {Skeleton} from "../../../api/types";
import {useEffect, useState} from "react";
import {agent} from "../../../api/agent";
import {convertToTime} from "../../utils/TimeUtil";
import styled from "styled-components";
import JobLogs from "./JobLogs";
import AssetsModal from "./AssetsModal";
import Status from "../../Status";
import Button from "../../Button";
import {useNavigate} from "react-router-dom";

interface Props {
    skeleton: Skeleton;
}

const JobItem = styled.div`
    width: 80%;
    background-color: var(--content-box-bg);
    box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;

    .progress {
        width: calc(100% - 2rem);
        background-color: #111116;
        margin: 0 1rem;

        &-bar {
            height: .3rem;
            background-color: var(--primary-blue);
            transition: width 0.3s ease-in-out;
        }
    }

    .products {
        padding: 0 1rem;
    }

    .assets {
        padding: .5rem 1rem;
    }

    .assets:hover {
        background-color: var(--content-box-hover);
    }

    .workers {
        display: grid;
        gap: 1rem;
        padding: 1rem;
        width: calc(100% - 2rem);
    }
    
    .confirmation {
        display: flex;
        justify-content: center;
    }
`;

const JobHeader = styled.div`
    padding: 1rem 2rem;
    display: grid;
    cursor: pointer;
    grid-template-columns: auto auto;

    &:hover {
        background-color: var(--content-box-hover);
    }

    h1 {
        margin: 0;
    }
`;

const Center = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 1rem;
`;

export default function JobDetail({skeleton}: Props) {

    const [logsOpen, setLogsOpen] = useState(false);
    const [assetsOpen, setAssetsOpen] = useState(false);
    const [now, setNow] = useState<Date>(new Date());

    useEffect(() => {
        if (skeleton.status === "running") {
            const interval = setInterval(() => setNow(new Date()), 100);
            return () => clearInterval(interval);
        } else {
            setNow(new Date(skeleton.end));
        }
    }, [skeleton.end, skeleton.status]);

    useEffect(() => {
        if (skeleton.status === "running" && !logsOpen) {
            setLogsOpen(true);
        }
    }, [skeleton.status, logsOpen]);

    const navigate = useNavigate();

    return (<>
        <AssetsModal open={assetsOpen} skeleton={skeleton} onClose={() => setAssetsOpen(false)}></AssetsModal>

        <Center>
            <JobItem>
                <JobHeader onClick={() => setLogsOpen(!logsOpen)}>
                    <div>
                        <h1 style={{display: 'inline-block'}}>{skeleton.repo.name}</h1>
                        <span style={{marginLeft: '.5rem'}}>{skeleton.repo.readable}</span>
                    </div>
                    <div style={{position: 'relative'}}>
                        <Status status={skeleton.status}></Status>
                    </div>
                    <div>
                        <span>{new Date(skeleton.date).toLocaleString()}</span>
                    </div>
                    <div style={{position: 'relative'}}>
                        <span style={{position: 'absolute', right: '0'}}>{skeleton.version}</span>
                    </div>
                    <div>
                        {(skeleton.status === 'running' || skeleton.status === 'waiting') && (
                            <a onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                agent.abortRun(skeleton.id).then();
                            }}>Abort</a>
                        )}
                        {(skeleton.status !== 'running' && skeleton.status !== 'waiting') && (
                            <a onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                agent.reRun(skeleton.id).then();
                            }}>Rerun</a>
                        )}
                    </div>
                    <div style={{position: 'relative'}}>
                                <span style={{
                                    position: 'absolute',
                                    right: '0'
                                }}>{convertToTime(skeleton.start, new Date(now).toISOString())}</span>
                    </div>
                </JobHeader>
                <div className={'products'}>
                    {skeleton.assets && skeleton.assets.length > 0 && (
                        <div className={'assets'} style={{float: 'right', cursor: 'pointer'}} onClick={(event) => {
                            setAssetsOpen(true);
                            //setAssetModal({id: skeleton.id, assets: skeleton.assets, folder: ''});
                        }}>
                            <i className="fa-solid fa-folder-open"></i>
                            <span style={{marginLeft: '.5rem'}}>{skeleton.assets.length} Assets</span>
                        </div>)}
                </div>
                {!skeleton.needsToBeConfirmed && (<><div className={'workers'}
                     style={{gridTemplateColumns: 'repeat(' + skeleton.workers.length + ', 1fr)'}}>
                    {skeleton.workers.map((worker, workerIndex) => (
                        <div key={workerIndex}>
                            <div style={{display: 'flex', padding: '.5rem', gap: '.5rem'}}>
                                <h2 style={{margin: '0'}}>{worker.name}</h2>
                                <span
                                    style={{marginTop: '.5rem'}}>{convertToTime(worker.start, worker.status === 'running' ? new Date().toISOString() : worker.end)}</span>
                                <div style={{position: 'relative'}}>
                                    <Status style={{
                                        position: 'relative',
                                        margin: '.3rem 0 0 0'
                                    }} status={worker.status}></Status>
                                </div>
                            </div>
                            <div className={'progress'}>
                                <div className={'progress-bar'} style={{
                                    width: (worker.progress * 1e2) + '%',
                                    transition: 'width ease-in-out .3s'
                                }}></div>
                            </div>

                        </div>
                    ))}
                </div>
                <JobLogs open={logsOpen} skeleton={skeleton}></JobLogs></>)}
                {skeleton.needsToBeConfirmed && (<div className={'confirmation'}>
                    <div>
                        <h1>
                            This job is waiting for confirmation to proceed.
                        </h1>
                        <div style={{display: 'flex', gap: '1rem', justifyContent: 'space-evenly', marginBottom: '1rem'}}>
                            <Button onClick={() => agent.putConfirm(skeleton.id)}>Confirm</Button>
                            <Button onClick={() => navigate('pipeline-builder?skeleton=' + skeleton.id)}>Show Config</Button>
                        </div>
                    </div>
                </div>)}
            </JobItem>
        </Center>
    </>);

}

